
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { MessageSquare, Loader2, Lightbulb, BugPlay, ThumbsUp } from "lucide-react";

type FormValues = {
  feedbackType: 'suggestion' | 'bug' | 'praise';
  subject: string;
  message: string;
};

type FeedbackFormProps = {
  children?: React.ReactNode;
};

export default function FeedbackForm({ children }: FeedbackFormProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      feedbackType: 'suggestion',
    }
  });
  
  const feedbackType = watch('feedbackType');
  
  const onSubmit = async (data: FormValues) => {
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      // Use the generic version of supabase client to bypass type checking
      const { error } = await (supabase as any)
        .from('feedback')
        .insert({
          user_id: user.id,
          feedback_type: data.feedbackType,
          subject: data.subject,
          message: data.message,
          created_at: new Date().toISOString(),
        });
        
      if (error) throw error;
      
      toast.success("Thank you for your feedback!", {
        position: "bottom-right",
        dismissible: true,
      });
      
      reset();
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.", {
        position: "bottom-right",
        dismissible: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFeedbackIcon = (type: string) => {
    switch(type) {
      case 'suggestion':
        return <Lightbulb className="h-5 w-5 text-amber-500" />;
      case 'bug':
        return <BugPlay className="h-5 w-5 text-red-500" />;
      case 'praise':
        return <ThumbsUp className="h-5 w-5 text-green-500" />;
      default:
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="ghost" className="text-sm flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Feedback
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
        <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 border-b">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-expitrack-primary flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Send Feedback
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              We appreciate your feedback to help improve ExpiTrack.
            </DialogDescription>
          </DialogHeader>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Feedback Type</Label>
            <RadioGroup 
              defaultValue="suggestion" 
              className="grid grid-cols-3 gap-2"
            >
              <div className={`relative flex flex-col items-center space-y-2 rounded-md border p-3 ${feedbackType === 'suggestion' ? 'bg-amber-50 border-amber-200' : 'hover:bg-gray-50'}`}>
                <RadioGroupItem 
                  value="suggestion" 
                  id="suggestion" 
                  className="sr-only" 
                  {...register("feedbackType")} 
                />
                <Lightbulb className="h-5 w-5 text-amber-500" />
                <Label htmlFor="suggestion" className="cursor-pointer text-sm font-medium">Suggestion</Label>
              </div>
              <div className={`relative flex flex-col items-center space-y-2 rounded-md border p-3 ${feedbackType === 'bug' ? 'bg-red-50 border-red-200' : 'hover:bg-gray-50'}`}>
                <RadioGroupItem 
                  value="bug" 
                  id="bug" 
                  className="sr-only" 
                  {...register("feedbackType")} 
                />
                <BugPlay className="h-5 w-5 text-red-500" />
                <Label htmlFor="bug" className="cursor-pointer text-sm font-medium">Issue/Bug</Label>
              </div>
              <div className={`relative flex flex-col items-center space-y-2 rounded-md border p-3 ${feedbackType === 'praise' ? 'bg-green-50 border-green-200' : 'hover:bg-gray-50'}`}>
                <RadioGroupItem 
                  value="praise" 
                  id="praise" 
                  className="sr-only" 
                  {...register("feedbackType")} 
                />
                <ThumbsUp className="h-5 w-5 text-green-500" />
                <Label htmlFor="praise" className="cursor-pointer text-sm font-medium">Praise</Label>
              </div>
            </RadioGroup>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-sm font-medium">Subject</Label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                {getFeedbackIcon(feedbackType)}
              </div>
              <Input
                id="subject"
                placeholder="Brief subject of your feedback"
                className="pl-10"
                {...register("subject", { required: "Subject is required" })}
              />
            </div>
            {errors.subject && <p className="text-red-500 text-sm">{errors.subject.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm font-medium">Details</Label>
            <Textarea
              id="message"
              placeholder="Please provide details about your feedback..."
              rows={4}
              className="min-h-[120px] resize-none"
              {...register("message", { required: "Message is required" })}
            />
            {errors.message && <p className="text-red-500 text-sm">{errors.message.message}</p>}
          </div>
          
          <DialogFooter className="pt-2 flex justify-between sm:justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
            </DialogClose>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-expitrack-primary hover:bg-expitrack-primary/90 rounded-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
